import { Fragment } from "react";
import {
  AlarmClock,
  AlarmClockCheck,
  Ban,
  CalendarPlus,
  CircleCheck,
  EyeOff,
  Heart,
  Flag,
  FolderPlus,
  ListChecks,
  LogOut,
  MoreHorizontal,
  Pencil,
  Share,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { Button, ButtonCountBadge } from "@/components/ui/button";
import { OverlayIconButton } from "@/components/shared/OverlayIconButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { placeLeafActions, type LeafActionId, type LeafPageType } from "@/features/navigation/leafPages";

/** What a page can do right now; `leafPages.ts` decides where it goes. */
export interface LeafAction {
  onSelect: () => void;
  disabled?: boolean;
  /** A count on the control, such as active reminders. */
  count?: number;
  label?: string;
  icon?: LucideIcon;
}

export type LeafActionSet = Partial<Record<LeafActionId, LeafAction>>;

/** How each action looks. `group` separates kinds of action in the dots. */
const ACTIONS: Record<LeafActionId, {
  label: string;
  icon: LucideIcon;
  countIcon?: LucideIcon;
  group: number;
  destructive?: boolean;
}> = {
  remind: { label: "Reminders", icon: AlarmClock, countIcon: AlarmClockCheck, group: 0 },
  collect: { label: "Add to collection", icon: FolderPlus, group: 0 },
  share: { label: "Share", icon: Share, group: 0 },
  calendar: { label: "Add to Calendar", icon: CalendarPlus, group: 0 },
  favorite: { label: "Add to favorites", icon: Heart, group: 1 },
  done: { label: "Mark as done", icon: CircleCheck, group: 1 },
  hide: { label: "Hide", icon: EyeOff, group: 3, destructive: true },
  "edit-title": { label: "Edit title", icon: Pencil, group: 1 },
  "edit-items": { label: "Edit items", icon: ListChecks, group: 1 },
  visibility: { label: "Change visibility", icon: Pencil, group: 2 },
  sensitive: { label: "Mark as potentially sensitive", icon: EyeOff, group: 2 },
  delete: { label: "Delete permanently", icon: Trash2, group: 2, destructive: true },
  remove: { label: "Remove from my stasht", icon: Trash2, group: 3, destructive: true },
  leave: { label: "Leave collection", icon: LogOut, group: 3, destructive: true },
  unfollow: { label: "Unfollow collection", icon: LogOut, group: 3, destructive: true },
  report: { label: "Report Content", icon: Flag, group: 4 },
  block: { label: "Block Content", icon: Ban, group: 4 },
};

const DESTRUCTIVE_ITEM = "text-destructive focus:text-destructive";

export function LeafActions({ page, actions, presentation = "header", onMenuOpenChange, onOverlay = false }: {
  page: LeafPageType;
  actions: LeafActionSet;
  presentation?: "header" | "menu";
  onMenuOpenChange?: (open: boolean) => void;
  onOverlay?: boolean;
}) {
  const placed = placeLeafActions(page, actions);
  const bar = presentation === "menu" ? [] : placed.bar;
  const menu = presentation === "menu" ? [...placed.bar, ...placed.menu] : placed.menu;
  if (bar.length === 0 && menu.length === 0) return null;

  return (
    <div className="action-row leaf-actions">
      {bar.map(id => {
        const action = actions[id]!;
        const { label: defaultLabel, icon, countIcon } = ACTIONS[id];
        const label = action.label ?? defaultLabel;
        const Icon = (action.count ? countIcon : undefined) ?? action.icon ?? icon;
        return (
          <Button
            key={id}
            type="button"
            variant="ghost"
            size="icon-control"
            onClick={action.onSelect}
            disabled={action.disabled}
            aria-label={action.count ? `${label}: ${action.count}` : label}
            title={label}
          >
            <Icon aria-hidden="true" />
            {!!action.count && <ButtonCountBadge>{action.count}</ButtonCountBadge>}
          </Button>
        );
      })}
      {menu.length > 0 && (
        <DropdownMenu onOpenChange={onMenuOpenChange}>
          <DropdownMenuTrigger asChild>
            {onOverlay ? (
            <OverlayIconButton aria-label="Item actions" title="More actions" onClick={event => { event.preventDefault(); event.stopPropagation(); }}>
              <MoreHorizontal aria-hidden="true" />
            </OverlayIconButton>
            ) : (
            <Button type="button" variant="ghost" size="icon-control" aria-label={presentation === "menu" ? "Item actions" : "More actions"} title="More actions" onClick={event => { event.preventDefault(); event.stopPropagation(); }}>
              <MoreHorizontal aria-hidden="true" />
            </Button>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={event => event.stopPropagation()}>
            {menu.map((id, index) => {
              const action = actions[id]!;
              const { label, icon: Icon, group, destructive } = ACTIONS[id];
              const ItemIcon = action.icon ?? Icon;
              const startsGroup = index > 0 && ACTIONS[menu[index - 1]].group !== group;
              return (
                <Fragment key={id}>
                  {startsGroup && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onAfterClose={action.onSelect}
                    disabled={action.disabled}
                    className={destructive ? DESTRUCTIVE_ITEM : undefined}
                  >
                    <ItemIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                    {action.label ?? label}
                  </DropdownMenuItem>
                </Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
